import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './init-global';
import './index.css';
import './print.css';
import '@frontend/i18n';
import { isLocalDeployment } from '@frontend/env';
import { sentry } from '@frontend/tracking';
import Root from './root';
import { getSentryConfig } from './third-party-scripts/sentry-config';

if (import.meta.env.MODE === 'production' && !isLocalDeployment()) {
  try {
    sentry.init(getSentryConfig());
    sentry.instance.addEventProcessor((event, hint) => {
      if (
        hint.originalException &&
        hint.originalException instanceof Error &&
        hint.originalException.message.match(/Failed to fetch data/i)
      ) {
        event.fingerprint = ['fetch-type-error'];
        event.tags = {
          topic: 'http',
        };
        event.contexts = {
          ...event.contexts,
          fetchData: {
            url: hint.originalException.message.split(': ')[1],
          },
        };
      }
      return event;
    });
  } catch (e) {
    console.error(e);
  }
} else {
  console.warn('Sentry is not initialized because the app is not running in production.');
}

const renderApp = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!);
  const disableStrictMode = import.meta.env.DISABLE_STRICT_MODE; // NOTE strict mode only affects dev builds and is compiled out of prod react builds (based on NODE_ENV var)
  if (disableStrictMode) {
    console.warn('React Strict Mode is disabled');
  }

  const app = (
    <Suspense fallback='loading'>
      <Root />
    </Suspense>
  );

  root.render(disableStrictMode ? app : <StrictMode>{app}</StrictMode>);
};

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.ready
    .then(() => console.log('Service Worker is active'))
    .catch((error) => console.error('Service Worker registration failed:', error))
    .finally(() => renderApp());

  const swUrl = import.meta.env.DEV
    ? import.meta.env.MODE === 'test'
      ? '/mockSoftphoneServiceWorker.js'
      : '/dev-sw.js?dev-sw'
    : '/service-worker.js';

  navigator.serviceWorker.register(swUrl);
} else {
  renderApp();
}
